import type { Strings } from './types'

// Spanish (es) dictionary — neutral US-Hispanic Spanish, informal "tú".
// Machine-translated; see I18N-REVIEW.md for the native-review checklist.
// "NanoRhino" is never translated; units (lb/kg/Cal/kcal) are kept as-is.
export const es: Strings = {
  hero: {
    methodologyLink: 'Ver la metodología →',
    badgesDefault: ['Mifflin-St Jeor', 'Katch-McArdle', 'MET Ainsworth 2011', 'Sin cuenta'],
    badgesGlp1: ['Meta de proteína', 'Preservación muscular', 'Piso de calorías seguro', 'Sin cuenta'],
  },
  disclaimer: {
    text: 'No es asesoramiento médico · Estimaciones, registra 2 semanas para calibrar · Gratis, sin cuenta',
    methodology: 'Metodología',
  },
  error: {
    headline: 'Mmm, eso no se envió.',
    busy: 'El servicio de IA está ocupado un momento — vuelve a enviar tu mensaje en unos segundos. Tu conversación está guardada.',
  },
  chat: {
    hintKicker: 'Entrevista con IA · Claude',
    hintMeta: '~60 segundos · 5–7 preguntas',
    steps: [
      { label: 'Tú y tu cuerpo', detail: 'edad, sexo, estatura, peso' },
      { label: 'Ocupación', detail: 'escritorio, activa, de pie' },
      { label: 'Movimiento diario', detail: 'pasos, transporte, hogar' },
      { label: 'Ejercicio', detail: 'qué, con qué frecuencia, qué tan fuerte' },
      { label: 'Estilo de dieta', detail: 'alta en proteína, baja en carbos, etc.' },
    ],
    exampleLead: 'O adelántate y escribe una línea:',
    exampleDefault: '"Hombre de 32 años, 5\'10", 176 lb, trabajo de escritorio, unos 6k pasos al día."',
    exampleGlp1: '"En Zepbound, mujer de 34, 5\'6", 190 lb — quiero perder grasa pero conservar mi músculo."',
    mutedDefault: 'También funciona en métrico (cm / kg).',
    mutedGlp1: 'Cualquier GLP-1 sirve — Ozempic, Wegovy, Mounjaro, retatrutida. También en métrico (cm / kg).',
    ariaStartDefault: 'Inicia tu cálculo de TDEE',
    ariaStartGlp1: 'Inicia tu cálculo de calorías y proteína con GLP-1',
    startPrimaryDefault: 'Iniciar mi cálculo de TDEE gratis',
    startPrimaryGlp1: 'Iniciar mi cálculo GLP-1',
    startChipMaintain: 'Mantener mi peso',
    startChipBuild: 'Ganar músculo',
    startChipRecomp: 'Recomposición / ganar músculo',
    startChipComingOff: 'Mantener / dejando el medicamento',
    seedLose: 'Quiero bajar de peso — empecemos mi cálculo de TDEE.',
    seedMaintain: 'Quiero mantener mi peso — empecemos mi cálculo de TDEE.',
    seedBuild: 'Quiero ganar músculo — empecemos mi cálculo de TDEE.',
    seedGlp1Start:
      'Estoy con un GLP-1 y quiero perder grasa conservando mi músculo — empecemos mi cálculo de calorías y proteína.',
    seedGlp1Recomp:
      'Estoy con un GLP-1 y hago pesas — quiero recomposición (perder grasa, ganar músculo). Empecemos mi cálculo.',
    seedGlp1ComingOff:
      'Estoy con un GLP-1 y quiero mantener o dejarlo — empecemos mi cálculo.',
    planOfferChip: 'Sí, arma mi plan',
    placeholderThinking: 'Pensando…',
    placeholderStart: 'Empieza la entrevista — p. ej. "hombre de 32, 5\'10", 176 lb, trabajo de escritorio"',
    placeholderAnswer: 'Escribe tu respuesta…',
    send: 'Enviar',
    srAnswer: 'Tu respuesta',
  },
  blocked: {
    title: 'Acceso en pausa',
    body: 'NanoRhino está pensado para personas adultas, así que no podemos calcular calorías ni crear un plan para alguien menor de 18 años. Si tienes 18 años o más y llegaste aquí por error, escríbenos y restauraremos tu acceso:',
    placeholder: 'Acceso en pausa — menor de 18',
  },
  result: {
    kicker: 'Tu rango de TDEE',
    tierTight: 'Rango estrecho',
    tierCalibrated: 'Calibrado',
    tierWide: 'Amplio — registra 2 semanas',
    unit: 'kcal/día',
    recommended: 'Recomendado',
    showWork: 'Ver el cálculo',
    hide: 'Ocultar',
    show: 'Mostrar',
    bmr: 'TMB',
    bmrMifflin: 'Mifflin-St Jeor: 10·kg + 6.25·cm − 5·años ± ajuste por sexo',
    bmrKatch: 'Katch-McArdle: 370 + 21.6 × masa magra',
    neat: 'NEAT',
    neatHint: 'Actividad diaria sin ejercicio',
    occupation: 'Ocupación',
    occupationHint: 'banda base según el rol',
    steps: 'Pasos',
    stepsHint: '~0.045 kcal × pasos × (kg/70); reemplaza la ocupación si es mayor',
    commute: 'Transporte',
    commuteHint: 'minutos × kcal/min según el medio',
    household: 'Hogar',
    householdHint: 'horas de tarea × banda de intensidad',
    tef: 'ETA',
    tefHint: 'efecto térmico de los alimentos · ajustado por estilo de dieta',
    eat: 'Ejercicio',
    eatHint: 'ejercicio deliberado',
    metCompendium: 'Compendio Ainsworth 2011',
    perWk: '/sem',
    recalc: 'Recalcular',
    recalcTitle: 'Borra esta conversación e inicia una nueva entrevista desde cero',
  },
  plan: {
    kicker: 'Tu plan',
    building: 'Armando…',
    errorPrefix: 'No se pudo armar tu plan: ',
    unknownError: 'error desconocido',
    tryAgain: 'Intentar de nuevo',
    skeleton: 'Personalizando tu plan a partir de tu TDEE…',
  },
  cta: {
    kickerReady: 'Tu plan está listo',
    kickerReadyGlp1: 'Tu plan GLP-1 está listo',
    textMeBtn: 'Envíame mi plan por texto →',
    fineprintUs: 'Gratis enviar el texto. Sin spam — te responde un coach de verdad.',
    emailInstead: 'O envíame el plan por correo',
    convoCaption: 'Así se ve por texto:',
    convoUser: '2 huevos, pan tostado, café solo',
    convoCoach: '~410 cal · 24g proteína — registrado. Te faltan 86g de proteína hoy.',
    noAppLead: 'Sin app, sin contar.',
    noAppBody:
      'Escribes lo que comiste — una foto o unas palabras — y tu coach hace los cálculos.',
    rangeLede: (tdee: string) =>
      `~${tdee} Cal/día es la estimación de la fórmula. Tu gasto real está en algún punto de aquí:`,
    rangeLedeNoNumber:
      'El número que acabas de recibir es la estimación de la fórmula — tu gasto real anda cerca, pero no exactamente ahí.',
    rangeCaption: 'mismos datos, cuerpos distintos',
    rangeAria: (low: string, high: string, rec: string) =>
      `Tu gasto real diario está entre ${low} y ${high} calorías; la fórmula eligió ${rec}.`,
    rangeBody:
      'Dos semanas de comidas por texto fijan el tuyo — y desde ahí tu meta se vuelve a medir cada domingo con lo que comiste y pesaste de verdad, así no se desvía sin que nadie lo note.',
    ledeEstimate:
      'El número que acabas de recibir es una estimación de fórmula — un punto de partida, no una medición.',
    bodySteady:
      'Tu plan mantiene una meta diaria, una meta de proteína y unos hábitos que sostienen tu semana.',
    glp1Lede:
      'Tu medicamento controla el apetito — el punto ciego es lo que realmente comes.',
    glp1Body:
      'Tu coach mide tu ingesta y tu proteína reales desde tus textos, para que el peso que pierdas sea grasa, no músculo.',
    convoPlanMeta: '~1 minuto después de enviar',
    convoCaptionWeek: 'Tu primera semana, por texto:',
    convoSundayMeta: 'Domingo · remedición semanal',
    convoCoachSunday:
      'Semana 2 ✓ bajaste 0,7 kg. Estás gastando por encima de la fórmula, así que la meta de esta semana sube. Mismo ritmo, un poco más de comida.',
    previewTileProtein: 'Proteína',
    previewTileMeals: 'Comidas',
    intlKicker: 'Que se mantenga',
    intlLede:
      'El número que acabas de recibir es una estimación de fórmula — la misma matemática de todas las calculadoras. Tu TDEE real solo se puede medir con tus comidas registradas y tus pesajes.',
    intlLedeSteady:
      'El número que acabas de recibir es una estimación de fórmula — la misma matemática de todas las calculadoras. Lo que importa ahora es un plan que puedas vivir día a día.',
    whatsappBtn: 'Recibe mi plan por WhatsApp →',
    whatsappMechanicHint: 'Abre WhatsApp con tu solicitud ya escrita — solo dale enviar.',
    qrCaption: 'Escanéalo con tu teléfono — WhatsApp se abre con tu solicitud lista para enviar.',
    qrDesktopLink: '¿Tienes WhatsApp en esta computadora? Ábrelo aquí',
    previewSender: 'NanoRhino',
    previewPlanLabel: 'Plan',
    titleGlp1: 'Tu plan GLP-1',
    titleMaintain: 'Tu plan de mantenimiento',
    titleGain: 'Tu plan de ganancia',
    titleRecomp: 'Tu plan de recomposición',
    titleLose: 'Tu plan para bajar de peso',
    heroMaintainLabel: 'Meta diaria de mantenimiento',
    heroDailyLabel: 'Meta diaria inicial',
    heroGlp1PlanLabel: 'Tu plan',
    heroGlp1Alt: 'Piso de proteína',
    heroGlp1Unit: 'alcánzalo incluso en días de poco apetito',
    heroNoneLabel: 'Meta diaria inicial',
    heroNoneAlt: 'Tu número',
    heroNoneUnit: 'Cal / día',
    heroActivityLabel: 'Come estable en',
    honestPaceWithDate: (date: string) =>
      `El ritmo más rápido que es seguro para tu cuerpo — ${date} es la fecha honesta.`,
    honestPaceNoDate: 'Fijamos el ritmo más rápido que es seguro para tu cuerpo.',
    ledeActivity:
      'Para tu cuerpo, lo seguro es un ancla estable de comidas más movimiento que crea el déficit.',
    unitPerDay: 'Cal / día',
    smsPrefill: (tokenLower: string) => `Hola — envíame mi plan. Soy ${tokenLower}`,
    pitchKicker: 'Coach con IA + nutricionista real',
    pitchHeadline: 'Ninguna calculadora acierta tu número exacto.',
    pitchHeadlineLose: '¿Haces todo bien y la báscula no se mueve?',
    pitchSubLose:
      'No es tu fuerza de voluntad. El gasto real de nuestros miembros salió ~500 Cal/día por debajo de lo que les dijeron las calculadoras — el plan estaba mal desde el primer día.',
    featRecognizerTitle: 'Medimos tu número real.',
    featRecognizerBodyLoop:
      'Toma una foto de cada comida — sin app, sin contar (más del 90% de precisión en nuestras pruebas). En 2 semanas sabemos lo que tu cuerpo gasta de verdad.',
    featRecognizerBodySteady:
      'Toma una foto de cada comida — sin app, sin contar (más del 90% de precisión en nuestras pruebas) — y ves lo que comes de verdad.',
    featNutritionistTitle: '¿Estancada? Entra una nutricionista real.',
    featNutritionistBody:
      'No solo IA — una nutricionista humana encuentra qué te frena y te da un ajuste concreto.',
    featResultsTitle: (amount: string) => `Nuestros miembros bajan una mediana de ${amount} al mes.*`,
    featResultsBody: 'Sin inyecciones ni pastillas — con la comida que ya comes.',
    claimBtn: 'Empezar mi plan →',
    claimBtnWhatsapp: 'Empezar mi plan por WhatsApp →',
    fineprintClaim: 'Nada que descargar — solo escribe.',
    resultsFootnote: '*Miembros activos que registran sus comidas, jul–sep 2026. Los resultados varían.',
    convoUserPhoto: '📷 foto del almuerzo',
    convoCoachPhoto: 'Bowl de burrito con pollo · ~640 cal · 42g proteína — registrado. Te quedan 1.180 hoy.',
    email: {
      placeholder: 'tú@ejemplo.com',
      submit: 'Enviarme el plan',
      sending: 'Enviando…',
      fineprint: 'Te escribimos una vez con tu plan. Sin marketing, sin spam.',
      errNoContext: 'Sin contexto de conversación — actualiza la página e intenta de nuevo.',
      errInvalid: 'Eso no parece un correo válido.',
      errFailed: 'el envío falló',
      confirmHeadline: '✅ Tu plan va en camino.',
    },
  },
  nav: {
    ariaPrimary: 'Principal',
    links: [
      { label: 'Cómo funciona', href: 'https://nanorhino.com/#how-it-works' },
      { label: 'Por qué funciona', href: 'https://nanorhino.com/#why' },
      { label: 'Precios', href: 'https://nanorhino.com/#pricing' },
      { label: 'Acerca de', href: 'https://nanorhino.com/about' },
      { label: 'Blog', href: 'https://nanorhino.com/blog' },
    ],
    coach: 'Escribe a tu coach',
    coachAria: 'Escribe a tu coach en nanorhino.com',
    openMenu: 'Abrir menú',
    closeMenu: 'Cerrar menú',
    legal: [
      { label: 'Política de privacidad', href: 'https://nanorhino.com/privacy' },
      { label: 'Términos de servicio', href: 'https://nanorhino.com/terms' },
      { label: 'Política de cookies', href: 'https://nanorhino.com/cookies' },
      { label: 'Soporte', href: 'https://nanorhino.com/support' },
    ],
  },
  footer: {
    tagline: 'Coach de nutrición con IA por mensaje de texto. Creado por Link Heart Limited en Houston, Texas.',
    productHeading: 'Producto',
    companyHeading: 'Empresa',
    legalHeading: 'Legal',
    product: [
      { label: 'Escribe a tu coach', href: 'https://nanorhino.com/message' },
      { label: 'Cómo funciona', href: 'https://nanorhino.com/#how-it-works' },
      { label: 'Precios', href: 'https://nanorhino.com/#pricing' },
      { label: 'Por qué funciona', href: 'https://nanorhino.com/#why' },
      { label: 'Calculadora de TDEE precisa', href: 'https://nanorhino.com/tdee' },
      { label: 'Calculadora de calorías y proteína GLP-1', href: 'https://nanorhino.com/tdee/glp1-calculator' },
      { label: 'EatPilot', href: 'https://eatpilot.app/' },
    ],
    company: [
      { label: 'Acerca de Link Heart Limited', href: 'https://nanorhino.com/about' },
      { label: 'Blog', href: 'https://nanorhino.com/blog' },
    ],
    legal: [
      { label: 'Política de privacidad', href: 'https://nanorhino.com/privacy' },
      { label: 'Términos de servicio', href: 'https://nanorhino.com/terms' },
      { label: 'Política de cookies', href: 'https://nanorhino.com/cookies' },
      { label: 'Soporte', href: 'https://nanorhino.com/support' },
    ],
    copyright: (year: number) => `© ${year} Link Heart Limited. Todos los derechos reservados.`,
  },
  rangeBar: (low, high, marker) =>
    `Rango de TDEE de ${low} a ${high} kcal por día, recomendado ${marker}`,
}
